import React, { useContext, useEffect, useState } from "react";
import { Table, Input, Form, Row, Col, Button, Tag } from "antd";
import { TDealer, TSpareDependency } from "../../../types";
import { ColumnsType } from "antd/es/table";
import DateUtils from "../../../utils/date";
import {
  dateFormat,
  dateOnlyFormat,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import {
  eFONTFAMILY,
  ePrivileges,
  eResultCode,
  eSPAREAPPROVALSTATUS,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditSpareDependency,
  GetSpareDepHistory,
  GetSpecificDealerData,
} from "../../../utils/api.constant";
import { TResponseModel } from "../../../types/config";
import useFetch from "../../../hooks/useFetch";
import { UserContext } from "../../../context";
import DealerDetails from "./dealer-details";
import Utils from "../../../utils";
import { CloseOutlined } from "@ant-design/icons";

type TProps = {
  onClose: Function;
  ticketId: number;
  dealerId: number;
  onRefreshList?: Function;
  isEditable?: boolean;
};

type TOtherDetails = {
  assignedToId: number;
  customerId: number;
  dealerId: number;
  id: number;
  productId: number;
  servicePartnerId: number;
  spareApprovalStatus: number;
};

const SparePartsTable = (props: TProps) => {
  const isEditable = props.isEditable ?? true; // Explicit fallback

  const context: any = useContext(UserContext);
  const [form] = Form.useForm();
  const { onShowToast } = useToast();
  const { post } = useFetch();
  const [data, setData] = useState<TSpareDependency[]>([]);
  const [otherData, setOtherData] = useState<TOtherDetails>();
  const [dynamicColumns, setDynamicColumns] = useState<
    ColumnsType<TSpareDependency>
  >([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [dealerDetails, setDealerDetails] = useState<TDealer>();

  useEffect(() => {
    fetchSpareDepHistory();
  }, [props.ticketId]);

  useEffect(() => {
    // if (!data?.[0]?.sparePriceList?.length) {
    //   setDisableSubmit(true);
    //   return;
    // }

    const lastStakeHolderName =
      data[0]?.sparePriceList?.[data[0]?.sparePriceList?.length - 1]
        ?.stakeHolderName;

    console.log("LAST STAKE HOLDER NAME--->>", lastStakeHolderName);

    switch (context?.user?.stakeHolderName) {
      case STAKE_HOLDERS.SERVICEPARTNER:
        if (data?.[0]?.sparePriceList?.length) {
          setDisableSubmit(true);
        }
        break;

      case STAKE_HOLDERS.CUSTOMER:
        if (
          lastStakeHolderName === STAKE_HOLDERS.CUSTOMER ||
          data?.[0]?.sparePriceList?.length === 0
        ) {
          setDisableSubmit(true);
        }
        break;

      case STAKE_HOLDERS.IDEX:
        if (
          lastStakeHolderName === STAKE_HOLDERS.IDEX ||
          data?.[0]?.sparePriceList?.length === 0
        ) {
          setDisableSubmit(true);
        }
        break;

      default:
        setDisableSubmit(false);
        break;
    }
  }, [context?.user, data]);

  useEffect(() => {
    generateDynamicColumns();
  }, [data]);

  useEffect(() => {
    fetchSpecificDealer();
  }, [props.dealerId]);

  const fetchSpecificDealer = async () => {
    if (props?.dealerId) {
      try {
        const payload = {
          data: {
            id: props?.dealerId,
          },
        };

        const response: TResponseModel = await post(
          GetSpecificDealerData,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            setDealerDetails(data);
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
    }
  };

  const fetchSpareDepHistory = async () => {
    try {
      const payload = {
        data: {
          ticketId: props.ticketId,
        },
      };

      const response: TResponseModel = await post(GetSpareDepHistory, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;

        if (returnCode === eResultCode.SUCCESS) {
          const { spareDependencyList, ...otherData } = data;

          // Initialize sparePrice from the last price in sparePriceList
          const updatedList = spareDependencyList.map(
            (item: { sparePriceList: string | any[] }) => ({
              ...item,
              sparePrice:
                item?.sparePriceList?.[item?.sparePriceList?.length - 1]
                  ?.price || undefined, // Use last price or undefined
            })
          );

          setOtherData(otherData);
          setData(updatedList);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handlePriceUpdate = (spareId: number, value: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.spareId === spareId
          ? { ...item, sparePrice: value ? parseFloat(value) : undefined }
          : item
      )
    );
  };

  const generateDynamicColumns = () => {
    if (!Array.isArray(data) || data.length === 0) {
      setDynamicColumns([]);
      return;
    }

    const baseColumns: ColumnsType<TSpareDependency> = [
      {
        title: "Product Name",
        dataIndex: "productName",
        key: "productName",
        width: 200,
      },
      {
        title: "Spare Part No",
        dataIndex: "sparePartNo",
        key: "sparePartNo",
        width: 150,
      },
      {
        title: "Spare Part Description",
        dataIndex: "sparePartDescription",
        key: "sparePartDescription",
        width: 300,
      },
    ];

    // Generate dynamic columns for stakeholder prices
    const stakeholderColumns =
      data[0]?.sparePriceList?.map((price, index) => ({
        title: (
          <>
            {price.stakeHolderName}{" "}
            {index === data[0]?.sparePriceList?.length - 1 ? (
              <Tag
                color={
                  otherData?.spareApprovalStatus ===
                  eSPAREAPPROVALSTATUS.PENDING
                    ? "#108ee9"
                    : otherData?.spareApprovalStatus ===
                        eSPAREAPPROVALSTATUS.APPROVED
                      ? "#0bb68c"
                      : otherData?.spareApprovalStatus ===
                          eSPAREAPPROVALSTATUS.REJECTED
                        ? "#f50"
                        : "black"
                }
              >
                {otherData?.spareApprovalStatus === eSPAREAPPROVALSTATUS.PENDING
                  ? "IN-PROGRESS"
                  : otherData?.spareApprovalStatus ===
                      eSPAREAPPROVALSTATUS.APPROVED
                    ? "APPROVED"
                    : otherData?.spareApprovalStatus ===
                        eSPAREAPPROVALSTATUS.REJECTED
                      ? "REJECTED"
                      : "N/A"}
              </Tag>
            ) : null}
            <br></br>
            {DateUtils.format(price.createdOn, dateFormat)}
          </>
        ),
        key: `stakeHolderPrice_${index}`,
        render: (_: any, record: TSpareDependency) => {
          const priceData = Array.isArray(record?.sparePriceList)
            ? record.sparePriceList?.[index]
            : null;
          return priceData ? `₹${priceData.price}` : "-";
        },
        width: 300,
      })) || [];

    // Updated Price column at the end
    const updatedPriceColumn = {
      title: "Price",
      key: "updatedPrice",
      width: 190,
      render: (_: TSpareDependency, record: TSpareDependency) => {
        return (
          <Input
            disabled={
              context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                ? true
                : false
            }
            placeholder="Price"
            type="number"
            value={record.sparePrice}
            onChange={(e) => {
              handlePriceUpdate(record.spareId, e.target.value);
            }}
          />
        );
      },
    };

    // Combine columns: base + dynamic + updatedPrice
    setDynamicColumns([
      ...baseColumns,
      ...stakeholderColumns,
      isEditable &&
      !disableSubmit &&
      otherData?.spareApprovalStatus !== eSPAREAPPROVALSTATUS.APPROVED
        ? updatedPriceColumn
        : {},
    ]);
  };

  const validateAndSubmit = async (
    status: number,
    shouldValidate: boolean = true
  ) => {
    // USER CAN SAVE THE RECORD WITH 0 OR NO PRICE
    // const invalidRows = data.filter((item) => !item.sparePrice);

    // if (invalidRows.length > 0 && shouldValidate) {
    //   onShowToast({
    //     type: ToastType.error,
    //     title: <CloseOutlined />,
    //     position: ToastOpen.centerTop,
    //     content: "Missing Spare Prices.",
    //   });
    // } else {
    try {
      const payload = {
        data: {
          ticketId: props.ticketId,
          ...otherData,
          spareDetails: data,
          spareApprovalStatus: status,
        },
      };

      const response: TResponseModel = await post(
        AddEditSpareDependency,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          props.onClose();
          props?.onRefreshList?.();
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
    // }
  };

  return (
    <div
      style={{
        maxHeight: "60vh",
        overflowY: "scroll",
        overflowX: "hidden",
        marginTop: !isEditable ? "2rem" : 0,
      }}
    >
      <Form form={form} layout="vertical">
        {isEditable ? (
          <DealerDetails
            selectedDealer={dealerDetails}
            needDefaultOpen={false}
          />
        ) : null}
        <Table
          columns={dynamicColumns}
          dataSource={data}
          rowKey="spareId"
          pagination={false}
        />
        {isEditable &&
        otherData?.spareApprovalStatus !== eSPAREAPPROVALSTATUS.APPROVED ? (
          <Row
            gutter={16}
            style={{ justifyContent: "center", marginTop: "2rem" }}
          >
            {context?.user?.stakeHolderName !== STAKE_HOLDERS.CUSTOMER ? (
              <Col>
                {!disableSubmit ? (
                  <Button
                    type="primary"
                    onClick={() =>
                      validateAndSubmit(eSPAREAPPROVALSTATUS.PENDING)
                    }
                    disabled={disableSubmit}
                  >
                    Submit
                  </Button>
                ) : null}
              </Col>
            ) : null}

            {/* DONE :: privileges checks are pending */}

            {Utils.isUserHasAccess(
              context?.user?.privilegeList,
              ePrivileges.APPROVESPARES
            ) && !disableSubmit ? (
              <Col>
                <Button
                  type="default"
                  onClick={() =>
                    validateAndSubmit(eSPAREAPPROVALSTATUS.APPROVED)
                  }
                >
                  Approve
                </Button>
              </Col>
            ) : null}

            {Utils.isUserHasAccess(
              context?.user?.privilegeList,
              ePrivileges.REJECTSPARES
            ) && !disableSubmit ? (
              <Col>
                <Button
                  type="default"
                  onClick={() =>
                    validateAndSubmit(eSPAREAPPROVALSTATUS.REJECTED, false)
                  }
                >
                  Reject
                </Button>
              </Col>
            ) : null}

            <Col>
              <Button type="primary" onClick={() => props.onClose()}>
                Close
              </Button>
            </Col>
          </Row>
        ) : null}
      </Form>
    </div>
  );
};

export default SparePartsTable;
